import * as React from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface UploadMultiImagesProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  itineraryId: string;
  activityId?: string; // Optional: if provided, saves to activities-data path
  label?: string;
  maxImages?: number;
  className?: string;
  hasError?: boolean;
}

const UploadMultiImages = React.forwardRef<HTMLDivElement, UploadMultiImagesProps>(
  ({ images, onImagesChange, itineraryId, activityId, label = "Upload Images", maxImages, className, hasError = false, ...props }, ref) => {
    const [uploading, setUploading] = React.useState(false);

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      setUploading(true);
      const uploadedUrls: string[] = [];

      try {
        for (const file of Array.from(files)) {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
          // Se è presente activityId, salva in activities, altrimenti in media
          const filePath = activityId 
            ? `itineraries/${itineraryId}/activities/${activityId}/media/${fileName}`
            : `itineraries/${itineraryId}/media/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('p2b')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error('Upload error:', error);
            alert(`Failed to upload ${file.name}: ${error.message}`);
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('p2b')
            .getPublicUrl(filePath);

          console.log('Generated public URL:', publicUrl);
          uploadedUrls.push(publicUrl);
        }

        const newImages = [...images, ...uploadedUrls];
        const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
        onImagesChange(finalImages);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('An error occurred while uploading images');
      } finally {
        setUploading(false);
      }
    };

    const removeImage = async (index: number) => {
      const imageUrl = images[index];
      
      // Try to delete from storage if it's a Supabase URL
      if (imageUrl && imageUrl.includes('supabase')) {
        try {
          // Extract file path from URL
          const urlParts = imageUrl.split('/storage/v1/object/public/p2b/');
          if (urlParts.length > 1) {
            const filePath = urlParts[1];
            await supabase.storage
              .from('p2b')
              .remove([filePath]);
          }
        } catch (error) {
          console.error('Error deleting image from storage:', error);
        }
      }

      const newImages = images.filter((_, i) => i !== index);
      onImagesChange(newImages);
    };

    const isMaxReached = maxImages ? images.length >= maxImages : false;

    return (
      <div ref={ref} className={className} {...props}>
        <div>
          <Label htmlFor="image-upload" className={`text-xs ${hasError ? "text-yellow-600" : ""}`}>{label}</Label>
          <div className="mt-2">
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              disabled={isMaxReached}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => document.getElementById('image-upload')?.click()}
              disabled={isMaxReached || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Uploading...' : isMaxReached ? `Max ${maxImages} images` : "Choose Images"}
            </Button>
          </div>
        </div>

        {/* Images Preview */}
        {images && images.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            {images.map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

UploadMultiImages.displayName = "UploadMultiImages";

export { UploadMultiImages };
